import { DomainUpsert } from "@/components/domain";
import { SelectSearch } from "@/components/select-search";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetAgencies } from "@/hooks/agencies";
import { useGetTours } from "@/hooks/tours";
import { CreateTourAgency, TourAgencyDomain } from "@/models";
import { FormOptions } from "@deadcow-enterprises/react-forms";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export function Form({
  model,
  onUpsert,
  domain,
  domainId,
  defaultCommissions,
}: {
  domainId: string;
  domain: TourAgencyDomain;
  model?: CreateTourAgency;
  defaultCommissions?: Omit<
    CreateTourAgency,
    "alias" | "year" | "agencyId" | "tourId"
  >;
  onUpsert: (data: CreateTourAgency) => Promise<void>;
}) {
  const { data: agencies, isLoading: agenciesLoading } = useGetAgencies({
    enabled: !model && domain === "tour",
  });
  const { data: tours, isLoading: toursLoading } = useGetTours({
    enabled: !model && domain === "agency",
  });
  const { t } = useTranslation();
  const isEdit = useMemo(() => !!model, [model]);
  const formConfig = useMemo<FormOptions<CreateTourAgency>>(() => {
    const config: FormOptions<CreateTourAgency> = {
      year: {
        value: model?.year ?? new Date().getFullYear(),
      },
      alias: {
        value: model?.alias ?? "",
      },
      agencyId: {
        value:
          domain === "agency" ? parseInt(domainId) : (model?.agencyId ?? -1),
        validator: z.number().min(0, "errors.required").optional(),
      },
      tourId: {
        value: domain === "tour" ? parseInt(domainId) : (model?.tourId ?? -1),
        validator: z.number().min(0, "errors.required").optional(),
      },
      bikesCommissions: {
        value: model?.bikesCommissions ?? defaultCommissions?.bikesCommissions,
      },
      extraNightsCommissions: {
        value:
          model?.extraNightsCommissions ??
          defaultCommissions?.extraNightsCommissions,
      },
      individualRoomSupplementCommission: {
        value:
          model?.individualRoomSupplementCommission ??
          defaultCommissions?.individualRoomSupplementCommission,
      },
      priceCommission: {
        value: model?.priceCommission ?? defaultCommissions?.priceCommission,
      },
      roomChangeCommissions: {
        value:
          model?.roomChangeCommissions ??
          defaultCommissions?.roomChangeCommissions,
      },
      seasonSupplementCommission: {
        value:
          model?.seasonSupplementCommission ??
          defaultCommissions?.seasonSupplementCommission,
      },
      servicesCommissions: {
        value:
          model?.servicesCommissions ?? defaultCommissions?.servicesCommissions,
      },
      soloTravelerCommission: {
        value:
          model?.soloTravelerCommission ??
          defaultCommissions?.soloTravelerCommission,
      },
    };
    return config;
  }, [model, domain, domainId, defaultCommissions]);

  return (
    <DomainUpsert
      title={model?.alias ?? t(`tourAgencies.${domain}.new.title`)}
      buttonTranslation={
        isEdit
          ? `tourAgencies.${domain}.update.button`
          : `tourAgencies.${domain}.new.button`
      }
      formConfig={formConfig}
      onUpsert={onUpsert}
      fields={[
        {
          key: "alias",
          label: "tourAgencies.fields.alias",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.alias ?? ""}
              onChange={(e) => {
                form.setValue("alias", e.target.value);
              }}
            />
          ),
        },
        !model && domain === "tour"
          ? {
              key: "agencyId",
              label: "tourAgencies.fields.agencyId",
              input: (form, id) =>
                agenciesLoading || !agencies ? (
                  <Skeleton className="h-9 w-full" />
                ) : (
                  <SelectSearch
                    id={id}
                    value={form.value.agencyId?.toString() ?? undefined}
                    onChange={(v) => {
                      form.setValue("agencyId", parseInt(v));
                      const currentAgency = agencies.find((a) => a.id === v);

                      form.setValue(
                        "priceCommission",
                        currentAgency?.priceCommission,
                      );
                      form.setValue(
                        "seasonSupplementCommission",
                        currentAgency?.seasonSupplementCommission,
                      );
                      form.setValue(
                        "individualRoomSupplementCommission",
                        currentAgency?.individualRoomSupplementCommission,
                      );
                      form.setValue(
                        "soloTravelerCommission",
                        currentAgency?.soloTravelerCommission,
                      );
                      form.setValue(
                        "bikesCommissions",
                        currentAgency?.bikesCommissions,
                      );
                      form.setValue(
                        "extraNightsCommissions",
                        currentAgency?.extraNightsCommissions,
                      );
                      form.setValue(
                        "roomChangeCommissions",
                        currentAgency?.roomChangeCommissions,
                      );
                      form.setValue(
                        "servicesCommissions",
                        currentAgency?.servicesCommissions,
                      );
                    }}
                    options={
                      agencies.map((a) => ({
                        value: a.id,
                        label: a.name,
                      })) ?? []
                    }
                  />
                ),
            }
          : null,
        !model && domain === "agency"
          ? {
              key: "tourId",
              label: "tourAgencies.fields.tourId",
              input: (form, id) =>
                toursLoading || !tours ? (
                  <Skeleton className="h-9 w-full" />
                ) : (
                  <SelectSearch
                    id={id}
                    value={form.value.tourId?.toString() ?? undefined}
                    onChange={(v) => {
                      form.setValue("tourId", parseInt(v));
                    }}
                    options={
                      tours.map((a) => ({
                        value: a.id,
                        label: a.name,
                      })) ?? []
                    }
                  />
                ),
            }
          : null,
        {
          key: "priceCommission",
          label: "tourAgencies.fields.priceCommission",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.priceCommission ?? ""}
              type="number"
              step={0.01}
              min={0}
              onChange={(e) => {
                form.setValue(
                  "priceCommission",
                  Number.parseFloat(e.target.value),
                );
              }}
            />
          ),
        },
        {
          key: "seasonSupplementCommission",
          label: "tourAgencies.fields.seasonSupplementCommission",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.seasonSupplementCommission ?? ""}
              type="number"
              step={0.01}
              min={0}
              onChange={(e) => {
                form.setValue(
                  "seasonSupplementCommission",
                  Number.parseFloat(e.target.value),
                );
              }}
            />
          ),
        },
        {
          key: "individualRoomSupplementCommission",
          label: "tourAgencies.fields.individualRoomSupplementCommission",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.individualRoomSupplementCommission ?? ""}
              type="number"
              step={0.01}
              min={0}
              onChange={(e) => {
                form.setValue(
                  "individualRoomSupplementCommission",
                  Number.parseFloat(e.target.value),
                );
              }}
            />
          ),
        },
        {
          key: "soloTravelerCommission",
          label: "tourAgencies.fields.soloTravelerCommission",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.soloTravelerCommission ?? ""}
              type="number"
              step={0.01}
              min={0}
              onChange={(e) => {
                form.setValue(
                  "soloTravelerCommission",
                  Number.parseFloat(e.target.value),
                );
              }}
            />
          ),
        },
        {
          key: "bikesCommissions",
          label: "tourAgencies.fields.bikesCommissions",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.bikesCommissions ?? ""}
              type="number"
              step={0.01}
              min={0}
              onChange={(e) => {
                form.setValue(
                  "bikesCommissions",
                  Number.parseFloat(e.target.value),
                );
              }}
            />
          ),
        },
        {
          key: "extraNightsCommissions",
          label: "tourAgencies.fields.extraNightsCommissions",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.extraNightsCommissions ?? ""}
              type="number"
              step={0.01}
              min={0}
              onChange={(e) => {
                form.setValue(
                  "extraNightsCommissions",
                  Number.parseFloat(e.target.value),
                );
              }}
            />
          ),
        },
        {
          key: "roomChangeCommissions",
          label: "tourAgencies.fields.roomChangeCommissions",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.roomChangeCommissions ?? ""}
              type="number"
              step={0.01}
              min={0}
              onChange={(e) => {
                form.setValue(
                  "roomChangeCommissions",
                  Number.parseFloat(e.target.value),
                );
              }}
            />
          ),
        },
        {
          key: "servicesCommissions",
          label: "tourAgencies.fields.servicesCommissions",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.servicesCommissions ?? ""}
              type="number"
              step={0.01}
              min={0}
              onChange={(e) => {
                form.setValue(
                  "servicesCommissions",
                  Number.parseFloat(e.target.value),
                );
              }}
            />
          ),
        },
      ]}
    />
  );
}
